import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import LocaleSwitcher from "./LocaleSwitcher";

const sections = [
  { id: "about", key: "about" },
  { id: "services", key: "services" },
  { id: "gallery", key: "gallery" },
  { id: "process", key: "process" },
  { id: "contact", key: "contact" },
];

export default function Nav() {
  const { t, locale, setLocale, locales } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-md bg-[#f9f8f5]/85 border-b border-[#e6e1d6]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1280px] mx-auto flex items-center justify-between px-6 md:px-10 py-4 md:py-5">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-serif-hero text-2xl md:text-[26px] tracking-tight text-[#252525]"
          aria-label="Tervalia"
        >
          Tervalia<span className="text-[#b86a55]">.</span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {sections.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-link-${s.id}`}
              onClick={() => goSection(s.id)}
              className="tv-nav-link"
            >
              {t.nav[s.key]}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LocaleSwitcher current={locale} setLocale={setLocale} locales={locales} />
          <button
            data-testid="nav-cta"
            onClick={() => goSection("contact")}
            className="tv-btn-nav"
          >
            {t.nav.cta}
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LocaleSwitcher current={locale} setLocale={setLocale} locales={locales} />
          <button
            data-testid="nav-mobile-toggle"
            className="text-[#252525] p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid="nav-mobile-drawer"
        className={`lg:hidden overflow-hidden bg-[#f9f8f5] border-b border-[#e6e1d6] transition-all duration-500 ${
          open ? "max-h-[640px]" : "max-h-0"
        }`}
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {sections.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-mobile-link-${s.id}`}
              onClick={() => goSection(s.id)}
              className="text-left tv-nav-link"
            >
              {t.nav[s.key]}
            </button>
          ))}
          <button data-testid="nav-mobile-cta" onClick={() => goSection("contact")} className="tv-btn-nav self-start mt-2">
            {t.nav.cta}
          </button>
        </div>
      </div>
    </header>
  );
}
